import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiButton, MiChip, MiDetailPageHeader } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ICONS_WEB,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { StepperView } from "../../../components/StepperView/StepperView";
import {
  ACTIVE_STEP,
  ACTIVE_STEPS,
  OPTION_STATUS,
  PLAN_STATUS,
} from "../../constants";
import { AddAreaTemplateModal } from "./SpecConfigForm/AddAreaTemplateModal/AddAreaTemplateModal";
import { SpecConfigForm } from "./SpecConfigForm/SpecConfigForm";
import { SpecDetailForm } from "./SpecDetailForm/SpecDetailForm";
import { SpecOptionsForm } from "./SpecOptionsForm/SpecOptionsForm";
import { useAddSpecSheetViewModel } from "./useAddSpecSheetViewModel";

const specDetailFormSchema = z.object({
  builderId: z.string().uuid(),
  seriesId: z.string().uuid("Series - Spec Level is required"),
  name: z.string().min(1, "Bid name is required"),
  specLevel: z.string().optional(),
  currentStep: z.number(),
});

function PlanStatusChip({ status }) {
  const theme = useTheme();
  const backgroundColors = {
    [PLAN_STATUS.DRAFT]: theme.palette.orange.main,
    [PLAN_STATUS.ACTIVE]: theme.palette.green.main,
    undefined: theme.palette.orange.main,
  };
  const statusText = {
    [PLAN_STATUS.DRAFT]: "Draft Mode",
    [PLAN_STATUS.ACTIVE]: "Active",
    undefined: "Draft Mode",
  };

  return (
    <MiChip
      label={statusText[status]}
      sx={{
        backgroundColor: backgroundColors[status],
        color: theme.palette.common.white,
        borderRadius: 1,
      }}
    />
  );
}

export function AddSpecSheet() {
  const [activeStep, setActiveStep] = useState(ACTIVE_STEP.DETAILS);
  const [disableNextStep, setDisableNextStep] = useState(false);
  const [requiredCategoriesMatch, setRequiredCategoriesMatch] = useState({});
  const [openAddAreaTemplateModal, setOpenAddAreaTemplateModal] =
    useState(false);

  const theme = useTheme();
  const { navigate, params, location } = useRouter();
  const seriesName = location?.state?.seriesDetail?.name || "";
  const accountName = location?.state?.accountDetail?.name || "";
  const { specPlanId, accountId, tab, seriesOrCommunityId } = params;
  const {
    getPlanDetails,
    handleCreateSpecPlan,
    stateManager,
    isLoading,
    planDetails,
    priceRollup,
    getPriceRollup,
    areaTypes,
    getAreaTypes,
    areaTemplates,
    getAreaTemplates,
    handleUpdateArea,
    handleAddAreas,
    handleRemoveArea,
    handleAddItems,
    handleRemoveFloor,
  } = useAddSpecSheetViewModel({ setRequiredCategoriesMatch });

  const {
    formState: { isValid },
    control,
    getValues,
    reset,
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      builderId: accountId,
      seriesId: seriesOrCommunityId,
      name: "",
      specLevel: "",
      planAreas: [],
      currentStep: 0,
    },
    resolver: zodResolver(specDetailFormSchema),
  });

  useEffect(() => {
    getAreaTypes();
  }, []);

  useEffect(() => {
    if (specPlanId) {
      getPlanDetails(specPlanId, activeStep);
    }
  }, [specPlanId]);

  useEffect(() => {
    if (planDetails.id) {
      reset(planDetails);
      navigate(
        `/estimating/bids/${accountId}/${tab}/${seriesOrCommunityId}/add-bid/${planDetails.id}`
      );
    }
    if (ACTIVE_STEPS.some((step) => step.value === planDetails.currentStep)) {
      setActiveStep(planDetails.currentStep);
    }
  }, [planDetails]);

  useEffect(() => {
    if ([ACTIVE_STEP.CONFIGURE, ACTIVE_STEP.OPTIONS].includes(activeStep)) {
      getPriceRollup(planDetails.id);
    }
  }, [activeStep, planDetails.planAreas?.length]);

  const handleSubmit = (step) => {
    const updatedPlanDetails = {
      ...planDetails,
      ...getValues(),
      currentStep: step,
    };
    handleCreateSpecPlan(updatedPlanDetails);
    if (planDetails.id) {
      setActiveStep(step);
    }
  };

  const onBack = () => {
    navigate(
      `/estimating/bids/${accountId}/${tab}/${seriesOrCommunityId}/all-bids`
    );
  };
  const { series, specLevel, name } = getValues();
  const mainTitle = specPlanId
    ? `${series} | ${specLevel || "--"} | ${name}`
    : `${accountName} | ${seriesName}`;
  const disableStep = isLoading || disableNextStep;
  const doRequiredCategoriesMatch = Object.values(
    requiredCategoriesMatch
  ).every((value) => value);

  const ACTIVE_STEP_CONFIG = {
    [ACTIVE_STEP.DETAILS]: {
      title: mainTitle,
      nextLabel: "Configure Bid",
      disabled: !isValid || isLoading,
      handleSubmit,
    },
    [ACTIVE_STEP.CONFIGURE]: {
      title: mainTitle,
      nextLabel: "Configure Options",
      handleSubmit,
      disabled:
        disableStep ||
        planDetails?.planAreas?.length === 0 ||
        !doRequiredCategoriesMatch,
    },
    [ACTIVE_STEP.OPTIONS]: {
      title: mainTitle,
      nextLabel: "Review",
      handleSubmit,
      disabled: disableStep,
    },
    [ACTIVE_STEP.REVIEW]: {
      title: mainTitle,
      nextLabel: "Submit",
      handleSubmit,
      disabled: disableStep,
    },
  };

  const selectedConfig = ACTIVE_STEP_CONFIG[activeStep] || {};
  const planDetailsForOptions = useMemo(() => {
    return {
      ...planDetails,
      planAreas:
        planDetails.planAreas?.map((area) => ({
          ...area,
          optionStatus: OPTION_STATUS.EXISTING,
          materials: area.materials?.map((material) => ({
            ...material,
            optionStatus: OPTION_STATUS.EXISTING,
          })),
        })) ?? [],
    };
  }, [planDetails]);

  const backgroundColors = {
    [PLAN_STATUS.DRAFT]: theme.palette.orange.light,
    [PLAN_STATUS.ACTIVE]: theme.palette.green.light,
    undefined: theme.palette.orange.light,
  };

  const titlePrefix = {
    // note: this is default for now until indication of plan import status is implemented
    [PLAN_STATUS.DRAFT]: "Create Bid",
    [PLAN_STATUS.ACTIVE]: "Edit Bid",
    undefined: "Create Bid",
  };

  return (
    <>
      <MiDetailPageHeader
        title={
          <Box display="flex" alignItems="center" gap={theme.spacing(2)}>
            <PlanStatusChip status={planDetails?.planStatus} />
            <Typography variant={TYPOGRAPHY_VARIANTS.TITLE}>
              {`${titlePrefix[planDetails?.planStatus]}${
                planDetails?.id ? ":" : ""
              }`}
            </Typography>
            <Box sx={{ display: "flex", maxHeight: 32 }}>
              {planDetails?.id ? (
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.TITLE}
                  color={theme.palette.cyan.dark}
                >
                  {selectedConfig?.title}
                </Typography>
              ) : (
                <Skeleton
                  variant="text"
                  sx={{
                    fontSize: "2rem",
                    display: !specPlanId ? "none" : "block",
                  }}
                  width={320}
                />
              )}
            </Box>
          </Box>
        }
        loading={isLoading}
        customOnBack={onBack}
        style={{ backgroundColor: backgroundColors[planDetails?.planStatus] }}
      />
      <StepperView
        activeStep={activeStep}
        setActiveStep={selectedConfig.handleSubmit}
        disabled={selectedConfig.disabled}
        disableNavigation
        steps={ACTIVE_STEPS}
        nextLabel={selectedConfig.nextLabel}
        customLeftBtn={
          activeStep === ACTIVE_STEP.CONFIGURE && (
            <MiButton
              title="Save As Area Template"
              startIcon={{ path: mdi[ICONS_WEB.SAVE] }}
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={() => setOpenAddAreaTemplateModal(true)}
              disabled={disableStep || planDetails?.planAreas?.length === 0}
            />
          )
        }
      >
        {activeStep === ACTIVE_STEP.DETAILS && (
          <SpecDetailForm
            stateManager={stateManager}
            control={control}
            setValue={setValue}
          />
        )}
        {activeStep === ACTIVE_STEP.CONFIGURE && (
          <SpecConfigForm
            stateManager={stateManager}
            isLoading={isLoading}
            activeStep={activeStep}
            planDetails={planDetails}
            priceRollup={priceRollup}
            areaTypes={areaTypes}
            areaTemplates={areaTemplates}
            getAreaTemplates={getAreaTemplates}
            setRequiredCategoriesMatch={setRequiredCategoriesMatch}
            handleAddItems={handleAddItems}
            handleAddAreas={handleAddAreas}
            handleUpdateArea={handleUpdateArea}
            handleRemoveArea={handleRemoveArea}
            handleRemoveFloor={handleRemoveFloor}
            handleCreateSpecPlan={handleCreateSpecPlan}
          />
        )}
        {activeStep === ACTIVE_STEP.OPTIONS && (
          <SpecOptionsForm
            stateManager={stateManager}
            activeStep={activeStep}
            planDetails={planDetailsForOptions}
            priceRollup={priceRollup}
            setDisableNextStep={setDisableNextStep}
          />
        )}
        {activeStep === ACTIVE_STEP.REVIEW && (
          <Box height="100%">
            <Typography variant="body2">Content not available.</Typography>
          </Box>
        )}
      </StepperView>
      <AddAreaTemplateModal
        stateManager={stateManager}
        open={openAddAreaTemplateModal}
        closeModal={() => setOpenAddAreaTemplateModal(false)}
        control={control}
        planDetails={{ ...planDetails, ...getValues() }}
        getAreaTemplates={getAreaTemplates}
      />
    </>
  );
}
